import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Send, MapPin } from 'lucide-react';
import CountUp from 'react-countup';
import { useLang } from '../context/LanguageContext';
import Particles from './Particles';

const LOGO = '/img/logo.png';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay },
});

export default function Hero() {
  const { t } = useLang();
  const h = t.hero;

  const handleScroll = () => {
    const el = document.querySelector('#about');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ background: '#0A0B0F' }}
    >
      {/* Cinematic Background Image */}
      <div className="hero-bg">
        <img 
          src="/img/photo_2026-03-14_14-36-10.jpg" 
          alt="KidsMafia Event Space" 
          className="hero-background"
        />
      </div>

      {/* Enhanced Background orbs */}
      <div
        className="orb-gold"
        style={{ width: 900, height: 900, top: '-20%', right: '-15%', opacity: 0.2, zIndex: 2 }}
      />
      <div
        className="orb-purple"
        style={{ width: 700, height: 700, bottom: '-15%', left: '-12%', opacity: 0.18, zIndex: 2 }}
      />
      <div
        className="orb-gold"
        style={{ width: 400, height: 400, bottom: '25%', right: '15%', opacity: 0.12, animationDelay: '5s', zIndex: 2 }}
      />

      {/* Particles */}
      <Particles count={100} />

      {/* Content */}
      <div
        className="relative z-10 max-w-6xl mx-auto px-6 text-center flex flex-col items-center"
        style={{ paddingTop: '6rem', paddingBottom: '2rem' }}
      >
        {/* Location tag */}
        <motion.div {...fadeUp(0.1)}>
          <div className="inline-flex items-center gap-2 glass-premium rounded-full px-5 py-2.5 mb-10">
            <MapPin size={16} className="text-[#D4AF37]" />
            <span className="text-[#E5E7EB] text-sm font-semibold tracking-widest uppercase">
              {h.tag}
            </span>
          </div>
        </motion.div>

        {/* Logo */}
        <motion.div {...fadeUp(0.2)} className="mb-10">
          <img 
            src={LOGO} 
            alt="KidsMafia" 
            className="h-24 md:h-28 lg:h-32 w-auto object-contain mx-auto icon-glow" 
          />
        </motion.div>

        {/* Headline - More Dramatic */}
        <motion.div {...fadeUp(0.3)}>
          <h1 className="font-heading leading-[0.9] tracking-tighter mb-6" style={{ fontFamily: "'Sora', sans-serif", fontWeight: 900, textShadow: '0 4px 30px rgba(0,0,0,0.8), 0 0 60px rgba(212,175,55,0.3)' }}>
            <span className="block text-5xl md:text-7xl lg:text-8xl text-white font-black" style={{ textShadow: '0 4px 30px rgba(0,0,0,0.8)' }}>
              {h.title_line1}
            </span>
            <span className="block text-5xl md:text-7xl lg:text-8xl text-white font-black" style={{ textShadow: '0 4px 30px rgba(0,0,0,0.8)' }}>
              {h.title_line2}
            </span>
            <span
              className="block text-6xl md:text-8xl lg:text-9xl font-black mt-4 gold-text"
              style={{ letterSpacing: '-0.03em' }}
            >
              {h.brand}
            </span>
          </h1>
        </motion.div>

        {/* Subtitle - Enhanced */}
        <motion.p
          {...fadeUp(0.45)}
          className="text-[#E5E7EB] text-xl md:text-2xl max-w-3xl mx-auto mt-6 mb-16 leading-relaxed font-medium"
          style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}
        >
          {h.subtitle}
        </motion.p>

        {/* Metrics - Premium Style */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-6 w-full max-w-4xl mb-16"
        >
          {/* Metric 1 */}
          <div
            data-testid="metric-investment"
            className="glass-premium rounded-3xl px-8 py-7 text-center card-lift"
            style={{ 
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.12) 0%, rgba(212, 175, 55, 0.04) 100%)',
              border: '1px solid rgba(212, 175, 55, 0.3)'
            }}
          >
            <p className="text-[#BFC3C9] text-xs uppercase tracking-widest mb-3 font-bold">
              {h.metric1_label}
            </p>
            <div className="counter-value text-4xl md:text-5xl font-black font-heading gold-text">
              <CountUp end={h.metric1_value} duration={2.5} separator=" " suffix={h.metric1_suffix} />
            </div>
            {h.metric1_unit && <p className="text-[#9CA3AF] text-xs mt-2 font-semibold">{h.metric1_unit}</p>}
          </div>

          {/* Metric 2 */}
          <div
            data-testid="metric-revenue"
            className="glass-premium rounded-3xl px-8 py-7 text-center card-lift-purple"
            style={{ 
              borderColor: 'rgba(107,78,255,0.3)', 
              background: 'linear-gradient(135deg, rgba(107,78,255,0.12) 0%, rgba(107,78,255,0.04) 100%)'
            }}
          >
            <p className="text-[#BFC3C9] text-xs uppercase tracking-widest mb-3 font-bold">
              {h.metric2_label}
            </p>
            <div className="text-4xl md:text-5xl font-black font-heading purple-text">
              <CountUp end={h.metric2_value} duration={2.5} separator=" " suffix={h.metric2_suffix} />
            </div>
            <p className="text-[#9CA3AF] text-xs mt-2 font-semibold">{h.metric2_unit}</p>
          </div>

          {/* Metric 3 */}
          <div
            data-testid="metric-profit"
            className="glass-premium rounded-3xl px-8 py-7 text-center card-lift"
            style={{ 
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.12) 0%, rgba(212, 175, 55, 0.04) 100%)',
              border: '1px solid rgba(212, 175, 55, 0.3)'
            }}
          >
            <p className="text-[#BFC3C9] text-xs uppercase tracking-widest mb-3 font-bold">
              {h.metric3_label}
            </p>
            <div className="counter-value text-4xl md:text-5xl font-black font-heading gold-text">
              <CountUp end={h.metric3_value} duration={2.5} separator=" " suffix={h.metric3_suffix} />
            </div>
          </div>
        </motion.div>

        {/* CTA Buttons - Enhanced */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="flex flex-col sm:flex-row gap-5 justify-center w-full max-w-xl"
        >
          <a
            href="tel:+34624586917"
            data-testid="hero-call-btn"
            className="btn-call flex items-center justify-center gap-3 px-10 py-5 rounded-full text-lg font-bold"
          >
            <Phone size={20} />
            {h.cta_call}
          </a>
          <a
            href="https://t.me/SerjValencia"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-telegram-btn"
            className="btn-telegram flex items-center justify-center gap-3 px-10 py-5 rounded-full text-lg font-bold"
          >
            <Send size={20} />
            {h.cta_telegram}
          </a>
        </motion.div>

        {/* Scroll Hint - Enhanced */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          onClick={handleScroll}
          className="mt-16 flex flex-col items-center gap-2 text-[#9CA3AF] hover:text-[#D4AF37] transition-colors duration-300 group cursor-pointer"
        >
          <span className="text-xs uppercase tracking-widest font-semibold">{h.scroll_hint}</span>
          <div className="w-6 h-10 rounded-full border-2 border-[#9CA3AF] group-hover:border-[#D4AF37] flex items-start justify-center p-2 transition-colors">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="w-1 h-2 rounded-full bg-[#D4AF37]"
            />
          </div>
        </motion.button>
      </div>
    </section>
  );
}
